import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { TFunction } from 'i18next';
import { strToTime } from '@/utils/strToTime';

export interface ParseedObj {
  poNumber: string;
  count: number;
  preTime: number;
  lotStr: string;
};

export const downloadTemplate = (t: TFunction) => {
  // 工作表数据（仅表头）
  const headers = [
    [t('purchase.purNo')],
    [t('purchase.excelCount')],
    [t('purchase.sendTime')],
    ['lot'],
  ];
  const worksheetData = [headers];

  // 创建工作表
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // 设置列宽
  worksheet['!cols'] = [

    { wch: 34 },  // 单号PO
    { wch: 12 },  // 送货数量
    { wch: 12 },  // 送货日期
    { wch: 24 },  // lot
  ];

  // 创建工作簿
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, t('purchase.inboundTemplate'));

  // 生成Excel文件并下载
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const dataBlob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
  });

  // 生成文件名（带时间戳）
  saveAs(dataBlob, `${t('purchase.inboundTemplate')}.xlsx`);
}

/**
 * 获取单元格值并trim
 */
const getCellValue = (row: any[], index: number): string => {
  if (row[index] === undefined || row[index] === null) return '';
  return String(row[index]).trim();
};


/**
 * 解析Excel模板文件
 * @param file 上传的Excel文件
 * @returns Promise<Record<string, ParseedObj>> 解析后的数据对象，以采购单号为key
 */
export const parseExcelTemplate = (file: File, t: TFunction): Promise<Record<string, ParseedObj>> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const errorLins: Array<string> = [];
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'array' });

        // 获取第一个工作表
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // 将工作表转换为数组格式（跳过表头）
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          raw: false,
        }) as any[][];

        // 跳过第一行表头，解析数据行
        const result: Record<string, ParseedObj> = {};
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];

          // 跳过空行
          if (!row || row.every((cell) => !cell)) continue;

          const poNumber = getCellValue(row, 0);
          const count = Number(getCellValue(row, 1)) || 0;
          const dateStr = getCellValue(row, 2);
          const lotStr = getCellValue(row, 3);

          // 解析日期为时间戳
          const preTime = strToTime(dateStr);

          if (poNumber.length !== 21) {
            errorLins.push(t('purchase.purNoErr', { index: i + 1 }))
            continue;
          }
          if (!!!count) {
            errorLins.push(t('purchase.countErr', { index: i + 1 }))
            continue;
          }
          if (preTime === 0) {
            errorLins.push(t('purchase.preTimeErr', { index: i + 1 }))
            continue;
          }

          let key = `${poNumber}*${preTime}*${lotStr}`;
          if (result[key]) {
            result[key].count = result[key].count + count
          } else {
            result[key] = {
              poNumber,
              count,
              preTime,
              lotStr,
            };
          }
        }
        if (errorLins.length > 0) reject({
          code: 10088,
          msg: errorLins
        })
        else resolve(result);
      } catch (error) {
        reject({
          code: 10088,
          msg: []
        });
      }
    };

    reader.onerror = () => {
      reject(10088);
    };

    reader.readAsArrayBuffer(file);
  });
};